// Página SaleForm - Registrar venta desde pedido
// Permite crear una venta a partir de un pedido completado

import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { getOrder } from '../../services/orders.service'
import { useSaleOperations, useSaleByOrderId } from '../../hooks/useSales'
import { useAuth } from '../../hooks/useAuth'
import { uploadImage } from '../../services/cloudinary.service'
import { getImageUrl } from '../../services/cloudinary.service'
import Button from '../../components/Button'
import Toast from '../../components/Toast'
import { formatCurrency, formatDate, formatCurrencyInput, parseCurrencyInput } from '../../utils/helpers'
import { notifyPaymentApproved } from '../../services/notifications.service'
import './SaleForm.css'

/**
 * Página SaleForm
 * Formulario para registrar una venta desde un pedido
 */
function SaleForm() {
  const { orderId } = useParams()
  const navigate = useNavigate()
  const { userData } = useAuth()
  const { create, update, loading: saving } = useSaleOperations()
  const { sale: existingSale, loading: loadingSale } = useSaleByOrderId(orderId)

  const [order, setOrder] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [notification, setNotification] = useState(null)
  const [editingFields, setEditingFields] = useState(new Set())

  // Estado del formulario
  const [formData, setFormData] = useState({
    subtotal: 0,
    discount: 0,
    taxes: 0,
    shipping: 0,
    total: 0,
    paymentMethod: 'pendiente',
    saleDate: new Date().toISOString().split('T')[0],
    notes: '',
    paymentProofs: [],
    paid: false // Estado de pago
  })

  const [uploadingImages, setUploadingImages] = useState(false)

  const isMercadoPagoSale =
    existingSale?.paymentGateway?.provider === 'mercadopago' ||
    order?.payment?.type === 'gateway_mercadopago' ||
    order?.paymentGateway?.provider === 'mercadopago'

  const isPaidLocked = !!(
    existingSale &&
    (existingSale.paid === true ||
      existingSale.status === 'completed' ||
      existingSale.paymentGateway?.status === 'approved')
  )

  const latestPayment =
    existingSale?.payments && existingSale.payments.length > 0
      ? existingSale.payments[existingSale.payments.length - 1]
      : null

  const getGatewayStatusLabel = (status) => {
    if (!status) return 'Estado de pago desconocido'
    if (status === 'approved') return 'Pago aprobado'
    if (status === 'in_process') return 'Pago en proceso'
    if (status === 'pending') return 'Pago pendiente'
    if (status === 'rejected') return 'Pago rechazado'
    if (status === 'cancelled') return 'Pago cancelado'
    if (status === 'refunded') return 'Pago reembolsado'
    return `Estado: ${status}`
  }

  // Cargar pedido
  useEffect(() => {
    const loadOrder = async () => {
      try {
        setLoading(true)
        const orderData = await getOrder(orderId)
        
        if (!orderData) {
          setError('Pedido no encontrado')
          return
        }

        setOrder(orderData)

        // Inicializar datos del formulario con datos del pedido
        setFormData(prev => ({
          ...prev,
          subtotal: orderData.subtotal != null ? String(orderData.subtotal) : '',
          shipping: orderData.shipping != null ? String(orderData.shipping) : '',
          total: Number(orderData.total || 0),
          saleDate: orderData.createdAt?.toDate
            ? orderData.createdAt.toDate().toISOString().split('T')[0]
            : new Date().toISOString().split('T')[0]
        }))
      } catch (err) {
        console.error('Error cargando pedido:', err)
        setError(err.message || 'Error al cargar el pedido')
      } finally {
        setLoading(false)
      }
    }

    if (orderId) {
      loadOrder()
    }
  }, [orderId])

  useEffect(() => {
    if (existingSale && !loadingSale && order) {
      const hasMercadoPagoGateway =
        existingSale.paymentGateway?.provider === 'mercadopago' ||
        existingSale.payment?.type === 'gateway_mercadopago' ||
        order?.payment?.type === 'gateway_mercadopago'

      const resolvedPaymentMethod =
        existingSale.paymentMethod && existingSale.paymentMethod !== 'pendiente'
          ? existingSale.paymentMethod
          : hasMercadoPagoGateway
            ? 'tarjeta'
            : 'pendiente'

      setFormData(prev => ({
        ...prev,
        subtotal: existingSale.subtotal != null
          ? String(existingSale.subtotal)
          : (order.subtotal != null ? String(order.subtotal) : ''),
        discount: existingSale.discount != null ? String(existingSale.discount) : '',
        taxes: existingSale.taxes != null ? String(existingSale.taxes) : '',
        shipping: existingSale.shipping != null
          ? String(existingSale.shipping)
          : (order.shipping != null ? String(order.shipping) : ''),
        total: Number(existingSale.total != null ? existingSale.total : (order.total || 0)),
        paymentMethod: resolvedPaymentMethod,
        paymentProofs: existingSale.paymentProofs || [],
        notes: existingSale.notes || '',
        paid:
          (existingSale.paymentGateway?.status === 'approved') ||
          existingSale.paid ||
          false,
        saleDate: existingSale.saleDate?.toDate
          ? existingSale.saleDate.toDate().toISOString().split('T')[0]
          : order.createdAt?.toDate
            ? order.createdAt.toDate().toISOString().split('T')[0]
            : new Date().toISOString().split('T')[0]
      }))
    }
  }, [existingSale, loadingSale, order])

  // Recalcular total cuando cambian los campos
  useEffect(() => {
    const subtotal = parseCurrencyInput(formData.subtotal || '0')
    const discount = parseCurrencyInput(formData.discount || '0')
    const taxes = parseCurrencyInput(formData.taxes || '0')
    const shipping = parseCurrencyInput(formData.shipping || '0')
    const total = subtotal - discount + taxes + shipping
    setFormData(prev => ({ ...prev, total: Math.max(0, total) }))
  }, [formData.subtotal, formData.discount, formData.taxes, formData.shipping])

  // Manejar cambios en el formulario
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    
    // Campos que deben ser strings
    const stringFields = ['notes', 'paymentMethod']
    
    // Campos de moneda
    const currencyFields = ['subtotal', 'discount', 'taxes', 'shipping', 'total']
    
    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }))
      return
    }
    
    if (stringFields.includes(name)) {
      setFormData(prev => ({ ...prev, [name]: value }))
      return
    }
    
    if (currencyFields.includes(name)) {
      // Permitir escribir números, punto, coma y $
      let cleanedValue = value.replace(/[^0-9.,]/g, '')
      
      if (!cleanedValue) {
        setFormData(prev => ({ ...prev, [name]: '' }))
        return
      }
      
      // Convertir punto a coma para decimales (formato argentino)
      cleanedValue = cleanedValue.replace('.', ',')
      
      // Validar que solo tenga una coma decimal
      const parts = cleanedValue.split(',')
      if (parts.length > 2) {
        cleanedValue = parts[0] + ',' + parts.slice(1).join('')
      }
      
      setFormData(prev => ({ ...prev, [name]: cleanedValue }))
      return
    }
    
    setFormData(prev => ({ ...prev, [name]: parseFloat(value) || 0 }))
  }

  // Manejar cuando el usuario hace foco en un campo de precio
  const handleCurrencyFocus = (e) => {
    const { name } = e.target
    setEditingFields(prev => new Set(prev).add(name))
  }

  // Manejar cuando el usuario termina de escribir (onBlur)
  const handleCurrencyBlur = (e) => {
    const { name } = e.target
    const value = formData[name]
    
    // Parsear y normalizar el valor
    const numericValue = parseCurrencyInput(value || '0')
    
    // Guardar como string numérico normalizado
    setFormData(prev => ({
      ...prev,
      [name]: numericValue > 0 ? numericValue.toString() : ''
    }))
    
    // Remover del conjunto de campos en edición
    setEditingFields(prev => {
      const newSet = new Set(prev)
      newSet.delete(name)
      return newSet
    })
  }

  // Manejar cambio de fecha
  const handleDateChange = (e) => {
    setFormData(prev => ({
      ...prev,
      saleDate: e.target.value
    }))
  }

  // Manejar subida de imágenes
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files)
    if (files.length === 0) return

    setUploadingImages(true)
    try {
      const uploadPromises = files.map(file => uploadImage(file, 'sales'))
      const uploadedImages = await Promise.all(uploadPromises)
      
      setFormData(prev => ({
        ...prev,
        paymentProofs: [...prev.paymentProofs, ...uploadedImages]
      }))

      setNotification({
        type: 'success',
        message: `${uploadedImages.length} imagen(es) subida(s) correctamente`
      })
      setTimeout(() => setNotification(null), 3000)
    } catch (err) {
      console.error('Error subiendo imágenes:', err)
      setNotification({
        type: 'error',
        message: 'Error al subir las imágenes'
      })
      setTimeout(() => setNotification(null), 5000)
    } finally {
      setUploadingImages(false)
      e.target.value = '' // Limpiar input
    }
  }

  // Eliminar imagen
  const handleRemoveImage = (index) => {
    setFormData(prev => ({
      ...prev,
      paymentProofs: prev.paymentProofs.filter((_, i) => i !== index)
    }))
  }

  // Validar formulario
  const validate = () => {
    if (!formData.total || Number(formData.total) <= 0) {
      setNotification({
        type: 'error',
        message: 'El total debe ser mayor a 0'
      })
      setTimeout(() => setNotification(null), 5000)
      return false
    }

    if (!formData.saleDate) {
      setNotification({
        type: 'error',
        message: 'La fecha de venta es requerida'
      })
      setTimeout(() => setNotification(null), 5000)
      return false
    }

    return true
  }

  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault()
    e.stopPropagation()

    if (!validate()) return
    if (saving) return

    try {
      const wasPaid = existingSale?.paid === true
      const numericSubtotal = parseCurrencyInput(formData.subtotal || '0')
      const numericDiscount = parseCurrencyInput(formData.discount || '0')
      const numericTaxes = parseCurrencyInput(formData.taxes || '0')
      const numericShipping = parseCurrencyInput(formData.shipping || '0')
      const numericTotal = numericSubtotal - numericDiscount + numericTaxes + numericShipping

      const saleData = {
        subtotal: numericSubtotal,
        discount: numericDiscount,
        taxes: numericTaxes,
        shipping: numericShipping,
        total: numericTotal,
        paymentMethod: formData.paymentMethod,
        paymentProofs: formData.paymentProofs,
        saleDate: new Date(formData.saleDate),
        notes: formData.notes.trim(),
        paid: formData.paid
      }

      const isNowPaid = saleData.paid === true
      const shouldNotifyPaymentApproved = !wasPaid && isNowPaid && order?.id

      let saleId
      
      if (existingSale) {
        // Si ya existe una venta, actualizarla
        await update(existingSale.id, saleData)
        saleId = existingSale.id
        if (shouldNotifyPaymentApproved) {
          notifyPaymentApproved(order.id).catch(() => null)
        }
        setNotification({
          type: 'success',
          message: 'Venta actualizada correctamente'
        })
      } else {
        // Si no existe, crear una nueva (aunque esto no debería pasar ya que se crea automáticamente)
        const newSaleData = {
          ...saleData,
          orderId: order.id,
          orderNumber: order.orderNumber,
          customer: order.customer,
          items: order.items,
          status: 'pending',
          createdBy: userData?.uid,
          createdByName: userData?.name || userData?.displayName || 'Sistema'
        }
        saleId = await create(newSaleData)
        if (shouldNotifyPaymentApproved) {
          notifyPaymentApproved(order.id).catch(() => null)
        }
        setNotification({
          type: 'success',
          message: 'Venta registrada correctamente'
        })
      }

      setTimeout(() => {
        navigate(`/admin/ventas/${saleId}`)
      }, 1500)
    } catch (error) {
      console.error('Error registrando venta:', error)
      setNotification({
        type: 'error',
        message: error.message || 'Error al registrar la venta'
      })
      setTimeout(() => setNotification(null), 5000)
    }
  }

  if (loading || loadingSale) {
    return (
      <div className="sale-form-page">
        <div className="loading-state">
          <p>Cargando datos...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="sale-form-page">
        <div className="error-state">
          <p>Error: {error}</p>
          <Link to="/admin/pedidos">
            <Button>Volver a Pedidos</Button>
          </Link>
        </div>
      </div>
    )
  }

  if (!order) {
    return (
      <div className="sale-form-page">
        <div className="error-state">
          <p>Pedido no encontrado</p>
          <Link to="/admin/pedidos">
            <Button>Volver a Pedidos</Button>
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="sale-form-page">
      <div className="form-header">
        <div>
          <h2>{existingSale ? 'Editar Venta' : 'Registrar Venta'}</h2>
          <p>Pedido: {order.orderNumber}</p>
          {existingSale && (
            <>
              <p style={{ color: 'var(--text-light)', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                Venta: {existingSale.saleNumber || existingSale.id}
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
                <span
                  className={`status-badge ${
                    existingSale.status === 'completed'
                      ? 'completed'
                      : existingSale.status === 'cancelled'
                        ? 'cancelled'
                        : existingSale.status === 'pending_delivery'
                          ? 'pending-delivery'
                          : existingSale.status === 'pending_payment'
                            ? 'pending-payment'
                            : 'pending'
                  }`}
                >
                  {existingSale.status === 'completed'
                    ? '✓ Venta Completada'
                    : existingSale.status === 'cancelled'
                      ? '✕ Venta Cancelada'
                      : existingSale.status === 'pending_delivery'
                        ? '📦 Pendiente Entrega'
                        : existingSale.status === 'pending_payment'
                          ? '⏳ Pendiente Pago'
                          : '⏱️ Pendiente'}
                </span>
                <span
                  className="status-badge"
                  style={{
                    background: existingSale.paid ? '#d4edda' : '#f8d7da',
                    color: existingSale.paid ? '#155724' : '#721c24'
                  }}
                >
                  {existingSale.paid ? '💵 Cobrada' : '⏳ Sin Cobrar'}
                </span>
                {isMercadoPagoSale && (
                  <span className="status-badge">
                    MP: {getGatewayStatusLabel(existingSale.paymentGateway?.status)}
                  </span>
                )}
              </div>
            </>
          )}
        </div>
        <Link to={`/admin/pedidos/${orderId}`}>
          <Button variant="outline">← Volver al Pedido</Button>
        </Link>
      </div>

      {/* Información del Pedido */}
      <div className="order-info-card">
        <h3>Información del Pedido</h3>
        <div className="info-grid">
          <div className="info-item">
            <label>Cliente:</label>
            <span>{order.customer?.name || '-'}</span>
          </div>
          <div className="info-item">
            <label>Email:</label>
            <span>{order.customer?.email || '-'}</span>
          </div>
          <div className="info-item">
            <label>Estado:</label>
            <span className="status-badge">{order.status}</span>
          </div>
          <div className="info-item">
            <label>Fecha del Pedido:</label>
            <span>{formatDate(order.createdAt)}</span>
          </div>
        </div>
      </div>

      {/* Formulario de Venta */}
      <form onSubmit={handleSubmit} className="sale-form">
        {/* Montos */}
        <div className="form-section">
          <h3 className="section-title">Montos</h3>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="subtotal">Subtotal</label>
              <input
                type="text"
                id="subtotal"
                name="subtotal"
                value={
                  editingFields.has('subtotal')
                    ? (formData.subtotal ? `$${String(formData.subtotal).replace('.', ',')}` : '')
                    : (formData.subtotal ? formatCurrencyInput(formData.subtotal, true) : '')
                }
                onChange={handleChange}
                onFocus={handleCurrencyFocus}
                onBlur={handleCurrencyBlur}
                disabled={saving || isPaidLocked}
                className="form-input"
                placeholder="$0"
              />
            </div>

            <div className="form-group">
              <label htmlFor="discount">Descuento</label>
              <input
                type="text"
                id="discount"
                name="discount"
                value={
                  editingFields.has('discount')
                    ? (formData.discount ? `$${String(formData.discount).replace('.', ',')}` : '')
                    : (formData.discount ? formatCurrencyInput(formData.discount, true) : '')
                }
                onChange={handleChange}
                onFocus={handleCurrencyFocus}
                onBlur={handleCurrencyBlur}
                disabled={saving || isPaidLocked}
                className="form-input"
                placeholder="$0"
              />
            </div>

            <div className="form-group">
              <label htmlFor="taxes">Impuestos</label>
              <input
                type="text"
                id="taxes"
                name="taxes"
                value={
                  editingFields.has('taxes')
                    ? (formData.taxes ? `$${String(formData.taxes).replace('.', ',')}` : '')
                    : (formData.taxes ? formatCurrencyInput(formData.taxes, true) : '')
                }
                onChange={handleChange}
                onFocus={handleCurrencyFocus}
                onBlur={handleCurrencyBlur}
                disabled={saving || isPaidLocked}
                className="form-input"
                placeholder="$0"
              />
            </div>

            <div className="form-group">
              <label htmlFor="shipping">Envío</label>
              <input
                type="text"
                id="shipping"
                name="shipping"
                value={
                  editingFields.has('shipping')
                    ? (formData.shipping ? `$${String(formData.shipping).replace('.', ',')}` : '')
                    : (formData.shipping ? formatCurrencyInput(formData.shipping, true) : '')
                }
                onChange={handleChange}
                onFocus={handleCurrencyFocus}
                onBlur={handleCurrencyBlur}
                disabled={saving || isPaidLocked}
                className="form-input"
                placeholder="$0"
              />
            </div>

            <div className="form-group total-group">
              <label htmlFor="total">Total</label>
              <input
                type="number"
                id="total"
                name="total"
                value={formData.total.toFixed(2)}
                readOnly
                disabled
                className="form-input total-input"
              />
              <div className="total-display">{formatCurrency(formData.total)}</div>
            </div>
          </div>
        </div>

        {/* Información de Pago */}
        <div className="form-section">
          <h3 className="section-title">Información de Pago</h3>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="paymentMethod">Método de Pago</label>
              <select
                id="paymentMethod"
                name="paymentMethod"
                value={formData.paymentMethod}
                onChange={handleChange}
                disabled={saving || isPaidLocked}
                className="form-input"
              >
                <option value="pendiente">Pendiente</option>
                <option value="efectivo">Efectivo</option>
                <option value="transferencia">Transferencia</option>
                <option value="tarjeta">Tarjeta</option>
                <option value="cheque">Cheque</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="saleDate">Fecha de Venta</label>
              <input
                type="date"
                id="saleDate"
                name="saleDate"
                value={formData.saleDate}
                onChange={handleDateChange}
                disabled={saving || isPaidLocked}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="paid"
                  checked={formData.paid}
                  onChange={handleChange}
                  disabled={saving || isPaidLocked}
                  className="form-checkbox"
                />
                <span>Venta cobrada</span>
              </label>
              <small className="form-hint">
                Marca esta casilla cuando la venta haya sido cobrada
              </small>
            </div>
          </div>
        </div>

        {/* Comprobantes de Pago / Información de Pasarela */}
        <div className="form-section">
          <h3 className="section-title">Comprobantes de Pago (Opcional)</h3>

          {isMercadoPagoSale && (
            <div className="mp-payment-info">
              <div className="mp-payment-header">
                <span className="mp-provider">MercadoPago</span>
                <span className="mp-status">
                  {getGatewayStatusLabel(existingSale?.paymentGateway?.status)}
                </span>
              </div>
              {latestPayment && (
                <div className="mp-payment-body">
                  {latestPayment.paymentId && (
                    <div className="mp-payment-row">
                      <span className="mp-label">ID de pago:</span>
                      <span className="mp-value">{latestPayment.paymentId}</span>
                    </div>
                  )}
                  {latestPayment.transactionAmount != null && (
                    <div className="mp-payment-row">
                      <span className="mp-label">Monto:</span>
                      <span className="mp-value">
                        {formatCurrency(latestPayment.transactionAmount || 0)}{' '}
                        {latestPayment.currencyId || ''}
                      </span>
                    </div>
                  )}
                  {latestPayment.receivedAt?.value && (
                    <div className="mp-payment-row">
                      <span className="mp-label">Fecha acreditación:</span>
                      <span className="mp-value">
                        {new Date(latestPayment.receivedAt.value).toLocaleString()}
                      </span>
                    </div>
                  )}
                  {existingSale?.paymentGateway?.statusDetail && (
                    <div className="mp-payment-row">
                      <span className="mp-label">Detalle estado:</span>
                      <span className="mp-value">{existingSale.paymentGateway.statusDetail}</span>
                    </div>
                  )}
                </div>
              )}
              <p className="mp-payment-hint">
                Usa el ID de pago para buscar la operación dentro de tu cuenta de MercadoPago.
              </p>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="paymentProofs" className="file-upload-label">
              {uploadingImages ? 'Subiendo...' : '📎 Subir Comprobantes'}
            </label>
            <input
              type="file"
              id="paymentProofs"
              accept="image/*"
              multiple
              onChange={handleImageUpload}
              disabled={saving || uploadingImages}
              className="file-input"
            />
            <small className="form-hint">
              Puedes subir múltiples imágenes como comprobantes de pago (por ejemplo, recibos de
              pagos presenciales o capturas de pantalla).
            </small>
          </div>

          {formData.paymentProofs.length > 0 && (
            <div className="images-preview">
              {formData.paymentProofs.map((image, index) => (
                <div key={index} className="image-preview-item">
                  <img
                    src={getImageUrl(image.fileId || image.url, { width: 200, height: 200, crop: 'fill' })}
                    alt={`Comprobante ${index + 1}`}
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveImage(index)}
                    disabled={saving}
                    className="remove-image-btn"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Notas */}
        <div className="form-section">
          <h3 className="section-title">Notas</h3>
          <div className="form-group">
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Notas adicionales sobre la venta..."
              rows="4"
              disabled={saving}
              className="form-textarea"
            />
          </div>
        </div>

        {/* Botones de acción */}
        <div className="form-actions">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(`/admin/pedidos/${orderId}`)}
            disabled={saving}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={saving || uploadingImages}
          >
            {saving ? (existingSale ? 'Actualizando...' : 'Registrando...') : (existingSale ? 'Actualizar Venta' : 'Registrar Venta')}
          </Button>
        </div>
      </form>

      {/* Notificaciones */}
      {notification && (
        <Toast
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}
    </div>
  )
}

export default SaleForm

